import React from 'react';

import { useService } from '@xstate/react';

import { productBuilderMachine } from '../../../context/ProductBuilder/machine';
import { getBaseProductType } from '../../../helpers/getBaseProductType';
import { getProductStateConfig } from '../../../helpers/getProductStateConfig';
import { isCurrentState } from '../../../helpers/isCurrentState';
import { throwError } from '../../../helpers/throwError';
import { ProductBuilderMachineEvent } from '../../../types';
import { Heading } from '../../Common/Heading';
import { Selector, SelectorImageItem } from '../../Common/Selector';
import { SectionContent } from './SectionContent';

type OnClick = (
  panelConfiguration: string,
  send: (event: ProductBuilderMachineEvent) => void,
) => () => void;

const onClick: OnClick = (panelConfiguration, send) => () => {
  send({
    type: 'UPDATE_CUSTOM',
    custom: {
      panelConfiguration,
    },
  });
};

export const PanelConfiguration = () => {
  const [state, send] = useService(productBuilderMachine);
  const productType = getBaseProductType(state.context);
  const {
    custom: { panelConfiguration, measurements },
  } = state.context;

  // Panel configuration depends on the width
  const [width, height] = measurements
    ? (measurements as string).split('x').map(value => parseInt(value))
    : ['', ''];

  // These are the current dimensions that each option is available for.
  // Ideally we will find a way to make this information available within the metafield data.
  let availableOptionsByDimensions = [
    {
      configuration: 'Left Opening',
      minWidth: 300,
      maxWidth: 960,
      panels: '1 Panel',
    },
    {
      configuration: 'Right Opening',
      minWidth: 300,
      maxWidth: 960,
      panels: '1 Panel',
    },
    {
      configuration: 'Left Fold',
      minWidth: 600,
      maxWidth: 1140,
      panels: '2 Panels',
    },
    {
      configuration: 'Right Fold',
      minWidth: 600,
      maxWidth: 1140,
      panels: '2 Panels',
    },
    {
      configuration: 'Center Opening',
      minWidth: 600,
      maxWidth: 1880,
      panels: '2 Panels',
    },
    {
      configuration: 'Left Fold + Right',
      minWidth: 900,
      maxWidth: 1690,
      panels: '3 Panels',
    },
    {
      configuration: 'Left + Right Fold',
      minWidth: 900,
      maxWidth: 1690,
      panels: '3 Panels',
    },
    {
      configuration: 'Left Fold, Post, Right',
      minWidth: 900,
      maxWidth: 1710,
      panels: '3 Panels',
    },
    {
      configuration: 'Left, Post, Right Fold',
      minWidth: 900,
      maxWidth: 1710,
      panels: '3 Panels',
    },
    {
      configuration: 'Center Opening, Post, Right',
      minWidth: 900,
      maxWidth: 2820,
      panels: '3 Panels',
    },
    {
      configuration: 'Left, Post + Center Opening',
      minWidth: 900,
      maxWidth: 2820,
      panels: '3 Panels',
    },
    {
      configuration: 'Center Opening Fold',
      minWidth: 1200,
      maxWidth: 2240,
      panels: '4 Panels',
    },
    {
      configuration: 'Center Opening, Post, Center Opening',
      minWidth: 1200,
      maxWidth: 3740,
      panels: '4 Panels',
    },
    {
      configuration: 'Left Opening, Post, Center Opening, Post, Right',
      minWidth: 1200,
      maxWidth: 3760,
      panels: '4 Panels',
    },
    {
      configuration: 'Left Fold + Right, Post, Center Opening',
      minWidth: 1500,
      maxWidth: 2810,
      panels: '5 Panels',
    },
    {
      configuration: 'Center Opening, Post, Left Opening + Right Fold',
      minWidth: 1500,
      maxWidth: 2810,
      panels: '5 Panels',
    },
    {
      configuration: 'Center Opening, Post, Center Opening, Post, Right',
      minWidth: 1500,
      maxWidth: 4680,
      panels: '5 Panels',
    },
    {
      configuration: 'Left Opening, Post, Center Opening, Post, Center Opening',
      minWidth: 1500,
      maxWidth: 4680,
      panels: '5 Panels',
    },
    {
      configuration: 'Left Fold + Right, Post, Left Fold + Right',
      minWidth: 1800,
      maxWidth: 3360,
      panels: '6 Panels',
    },
    {
      configuration: 'Left Fold + Right, Post, Left + Right Fold',
      minWidth: 1800,
      maxWidth: 3360,
      panels: '6 Panels',
    },
    {
      configuration: 'Left + Right Fold, Post, Left + Right Fold',
      minWidth: 1800,
      maxWidth: 3360,
      panels: '6 Panels',
    },
    {
      configuration: 'Left, Post, Center Opening Fold, Post, Right',
      minWidth: 1800,
      maxWidth: 3380,
      panels: '6 Panels',
    },
    {
      configuration:
        'Center Opening, Post, Center Opening, Post, Center Opening',
      minWidth: 1800,
      maxWidth: 5600,
      panels: '6 Panels',
    },
  ];

  const optionsAvailableAfterUserInput =
    typeof width === 'number'
      ? availableOptionsByDimensions.filter(
          option => option.minWidth <= width && option.maxWidth >= width,
        )
      : [];

  const availablePanelConfigurations = Array.from(
    new Set(
      optionsAvailableAfterUserInput.filter(option => {
        return state.context.custom.numberOfPanels == option.panels;
      }),
    ),
  );

  if (
    isCurrentState('panelConfiguration', state) &&
    availablePanelConfigurations.length === 0
  ) {
    throwError(state);
  }

  return (
    <SectionContent>
      <Heading>Panel Configuration</Heading>
      <Selector isPanel={true}>
        {availablePanelConfigurations.map(option => {
          const { showIcon, Icon, description } = getProductStateConfig(
            productType,
            'panelConfiguration',
            option.configuration,
          );
          return (
            <SelectorImageItem
              isSelected={option.configuration === panelConfiguration}
              onClick={onClick(option.configuration, send)}
              showIcon={showIcon}
              Icon={Icon}
              title={option.configuration}
              key={option.configuration}
              description={description}
            />
          );
        })}
      </Selector>
    </SectionContent>
  );
};
